import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Truck, Plus, Search, Filter, Download, Upload, ChevronLeft, ChevronRight,
  MoreVertical, Eye, Edit, Trash2, CheckCircle, XCircle, Clock, AlertTriangle,
  Package, Database, ArrowLeft, ArrowRight, Send, Receive, RotateCcw, Loader2, Minus, ChevronDown
} from 'lucide-react'
import { cn, formatCurrency, formatNumber, formatDate } from '../../lib/utils'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { inventoryApi, queryKeys } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { Dropdown } from '../../components/ui/Dropdown'
import { Skeleton } from '../../components/ui/Skeleton'
import { Table } from '../../components/ui/Table'
import { Pagination } from '../../components/ui/Pagination'
import { useUIStore } from '../../stores/uiStore'
import { useAuthStore , usePermissions } from '../../stores/authStore'
import toast from 'react-hot-toast'

const statusConfig = {
  pending: { label: 'در انتظار', variant: 'warning' as const, icon: Clock },
  in_transit: { label: 'در راه', variant: 'info' as const, icon: Truck },
  completed: { label: 'تکمیل شده', variant: 'success' as const, icon: CheckCircle },
  cancelled: { label: 'لغو شده', variant: 'danger' as const, icon: XCircle },
}

export default function StockTransfersPage() {
  const { hasPermission } = usePermissions()
  const { modals, openModal, closeModal } = useUIStore()
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<TransferListParams>({
    page: 1,
    page_size: 20,
    sort_by: 'created_at',
    sort_order: 'desc',
  })
  const [deletedId, setDeletedId] = useState<string | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.transfers(filters),
    queryFn: () => inventoryApi.listTransfers(filters),
    placeholderData: (prev) => prev,
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => inventoryApi.createTransfer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.transfers() })
      toast.success('انتقال انبار ایجاد شد')
      closeModal('create-transfer')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'ایجاد ناموفق بود'),
  })

  const completeMutation = useMutation({
    mutationFn: (id: string) => inventoryApi.completeTransfer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.transfers() })
      toast.success('انتقال تکمیل شد')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'تکمیل ناموفق بود'),
  })

  const cancelMutation = useMutation({
    mutationFn: (id: string) => inventoryApi.cancelTransfer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.transfers() })
      toast.success('انتقال لغو شد')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'لغو ناموفق بود'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => inventoryApi.deleteTransfer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.transfers() })
      toast.success('انتقال حذف شد')
      closeModal('delete-confirm')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'حذف ناموفق بود'),
  })

  const handlePageChange = (page: number) => setFilters(prev => ({ ...prev, page }))
  const handlePageSizeChange = (size: number) => setFilters(prev => ({ ...prev, page_size: size, page: 1 }))
  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); setFilters(prev => ({ ...prev, page: 1 })) }

  const totalPages = data?.total_pages || 1
  const totalItems = data?.total || 0

  const getRowDropdownItems = (transfer: any) => [
    { label: 'مشاهده', icon: Eye, onClick: () => window.open(`/inventory/transfers/${transfer.id}`, '_blank') },
    { label: 'ویرایش', icon: Edit, onClick: () => {}, disabled: !hasPermission('stock', 'transfer') || transfer.status !== 'pending' },
    { type: 'divider' },
    { label: 'تکمیل', icon: CheckCircle, onClick: () => completeMutation.mutate(transfer.id), disabled: !hasPermission('stock', 'transfer') || transfer.status !== 'in_transit' },
    { label: 'لغو', icon: XCircle, className: 'text-warning-600', onClick: () => cancelMutation.mutate(transfer.id), disabled: !hasPermission('stock', 'transfer') || transfer.status === 'completed' || transfer.status === 'cancelled' },
    { type: 'divider' },
    { label: 'حذف', icon: Trash2, className: 'text-danger-600', onClick: () => { setDeletedId(transfer.id); openModal('delete-confirm') }, disabled: !hasPermission('stock', 'transfer') || transfer.status !== 'pending' },
  ]

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">انتقالات انبار</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">
            {totalItems.toLocaleString('fa-IR')} انتقال ثبت شده
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => openModal('create-transfer')} disabled={!hasPermission('stock', 'transfer')}>
            <Plus className="w-4 h-4" />
            انتقال جدید
          </Button>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card p-4"
      >
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <Input placeholder="جستجو (شماره انتقال، انبار مبدأ/مقصد)..." className="flex-1 max-w-md" />
          <Select
            value={filters.status || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, status: v || undefined, page: 1 }))}
            options={[
              { value: '', label: 'همه وضعیت‌ها' },
              { value: 'pending', label: 'در انتظار' },
              { value: 'in_transit', label: 'در راه' },
              { value: 'completed', label: 'تکمیل شده' },
              { value: 'cancelled', label: 'لغو شده' },
            ]}
            placeholder="وضعیت"
            className="w-36"
          />
          <Select
            value={filters.from_warehouse_id || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, from_warehouse_id: v || undefined, page: 1 }))}
            options={[{ value: '', label: 'همه انبارهای مبدأ' }]}
            placeholder="انبار مبدأ"
            className="w-40"
          />
          <Select
            value={filters.to_warehouse_id || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, to_warehouse_id: v || undefined, page: 1 }))}
            options={[{ value: '', label: 'همه انبارهای مقصد' }]}
            placeholder="انبار مقصد"
            className="w-40"
          />
          <Dropdown
            trigger={<Button variant="outline"><Filter className="w-4 h-4 ml-1" /> فیلترها</Button>}
            items={[
              { label: 'خروجی اکسل', icon: Download, onClick: () => {} },
              { type: 'divider' },
              { label: 'وارد کردن', icon: Upload, onClick: () => {} },
            ]}
          />
        </form>
      </motion.div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card overflow-hidden"
      >
        {isLoading ? (
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>شماره انتقال</th>
                    <th className="hidden sm:table-cell">تاریخ</th>
                    <th className="hidden md:table-cell">انبار مبدأ</th>
                    <th className="hidden lg:table-cell">انبار مقصد</th>
                    <th>آیتم‌ها</th>
                    <th>مجموع تعداد</th>
                    <th>وضعیت</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td><Skeleton variant="text" width="120px" /></td>
                      <td className="hidden sm:table-cell"><Skeleton variant="text" width="80px" /></td>
                      <td className="hidden md:table-cell"><Skeleton variant="text" width="100px" /></td>
                      <td className="hidden lg:table-cell"><Skeleton variant="text" width="100px" /></td>
                      <td><Skeleton variant="text" width="60px" /></td>
                      <td><Skeleton variant="text" width="80px" /></td>
                      <td><Skeleton variant="rectangular" width="80px" height="24px" /></td>
                      <td><Skeleton variant="rectangular" width="32px" height="32px" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertTriangle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
            <p className="text-danger-600 dark:text-danger-400">خطا در بارگذاری انتقالات</p>
            <Button variant="outline" onClick={() => refetch()} className="mt-4">تلاش مجدد</Button>
          </div>
        ) : data?.items.length === 0 ? (
          <div className="p-12 text-center">
            <Truck className="w-16 h-16 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">هیچ انتقالی یافت نشد</h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-4">اولین انتقال انبار را ایجاد کنید</p>
            <Button onClick={() => openModal('create-transfer')} disabled={!hasPermission('stock', 'transfer')}>
              <Plus className="w-4 h-4 ml-1" />
              ایجاد انتقال
            </Button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>شماره انتقال</th>
                    <th className="hidden sm:table-cell">تاریخ</th>
                    <th className="hidden md:table-cell">انبار مبدأ</th>
                    <th className="hidden lg:table-cell">انبار مقصد</th>
                    <th>آیتم‌ها</th>
                    <th>مجموع تعداد</th>
                    <th>وضعیت</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.items || []).map((transfer, index) => {
                    const statusInfo = statusConfig[transfer.status as keyof typeof statusConfig] || statusConfig.pending
                    return (
                      <motion.tr
                        key={transfer.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                      >
                        <td className="font-medium text-neutral-900 dark:text-white font-mono">{transfer.transfer_number}</td>
                        <td className="hidden sm:table-cell text-neutral-600 dark:text-neutral-400 text-sm">{formatDate(transfer.created_at)}</td>
                        <td className="hidden md:table-cell text-neutral-700 dark:text-neutral-300">{transfer.from_warehouse?.name || '—'}</td>
                        <td className="hidden lg:table-cell text-neutral-700 dark:text-neutral-300">{transfer.to_warehouse?.name || '—'}</td>
                        <td className="font-mono text-sm text-neutral-700 dark:text-neutral-300">{transfer.lines?.length || 0}</td>
                        <td className="font-mono text-sm text-neutral-700 dark:text-neutral-300">
                          {formatNumber(transfer.lines?.reduce((s: number, l: any) => s + l.quantity, 0) || 0)}
                        </td>
                        <td>
                          <Badge variant={statusInfo.variant} className="text-xs flex items-center gap-1">
                            <statusInfo.icon className="w-3 h-3" />
                            {statusInfo.label}
                          </Badge>
                        </td>
                        <td>
                          <Dropdown
                            trigger={<button className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"><MoreVertical className="w-4 h-4" /></button>}
                            items={getRowDropdownItems(transfer)}
                          />
                        </td>
                      </motion.tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                نمایش {((filters.page - 1) * filters.page_size) + 1} تا {Math.min(filters.page * filters.page_size, totalItems)} از {totalItems.toLocaleString('fa-IR')}
              </div>
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                showPageSize
                pageSize={filters.page_size}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </>
        )}
      </motion.div>

      {/* Create Transfer Modal */}
      <Modal
        isOpen={modals['create-transfer']}
        onClose={() => closeModal('create-transfer')}
        title="ایجاد انتقال انبار جدید"
        size="lg"
      >
        <TransferForm onSubmit={createMutation.mutate} onClose={() => closeModal('create-transfer')} isLoading={createMutation.isPending} />
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={modals['delete-confirm']}
        onClose={() => { closeModal('delete-confirm'); setDeletedId(null) }}
        title="حذف انتقال انبار"
        size="sm"
      >
        <p className="text-neutral-600 dark:text-neutral-400">
          آیا از حذف این انتقال انبار مطمئن هستید؟ این عملیات قابل بازگشت نیست.
        </p>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => { closeModal('delete-confirm'); setDeletedId(null) }}>انصراف</Button>
          <Button variant="danger" onClick={() => deletedId && deleteMutation.mutate(deletedId)} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? <> <Loader2 className="w-4 h-4 animate-spin" /> در حال حذف... </> : 'حذف' }
          </Button>
        </div>
      </Modal>
    </div>
  )
}

const transferItemSchema = z.object({
  product_id: z.string().uuid('محصول معتبر نیست'),
  variant_id: z.string().uuid().optional().nullable(),
  quantity: z.number().min(1, 'تعداد باید حداقل ۱ باشد'),
})

const transferFormSchema = z.object({
  from_warehouse_id: z.string().uuid('انبار مبدأ معتبر نیست'),
  to_warehouse_id: z.string().uuid('انبار مقصد معتبر نیست'),
  notes: z.string().optional(),
  items: z.array(transferItemSchema).min(1, 'حداقل یک آیتم باید وجود داشته باشد'),
}).refine(data => data.from_warehouse_id !== data.to_warehouse_id, {
  message: 'انبار مبدأ و مقصد نمی‌توانند یکسان باشند',
  path: ['to_warehouse_id'],
})

type TransferFormData = z.infer<typeof transferFormSchema>

interface TransferFormProps {
  onSubmit: (data: TransferFormData) => void
  onClose: () => void
  isLoading: boolean
}

function TransferForm({ onSubmit, onClose, isLoading }: TransferFormProps) {
  const methods = useForm<TransferFormData>({
    resolver: zodResolver(transferFormSchema),
    defaultValues: {
      from_warehouse_id: '',
      to_warehouse_id: '',
      notes: '',
      items: [{ product_id: '', variant_id: null, quantity: 1 }],
    },
  })

  const { control, watch } = methods
  const { fields: itemFields, append: appendItem, remove: removeItem } = useFieldArray({
    control,
    name: 'items',
  })

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller
          name="from_warehouse_id"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select label="انبار مبدأ *" options={[{ value: '', label: 'انتخاب کنید' }]} placeholder="انبار مبدأ" {...field} />
          )}
        />
        <Controller
          name="to_warehouse_id"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select label="انبار مقصد *" options={[{ value: '', label: 'انتخاب کنید' }]} placeholder="انبار مقصد" {...field} />
          )}
        />
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <Input label="یادداشت‌ها" placeholder="اختیاری" {...field} className="md:col-span-2" />
          )}
        />
      </div>

      <div className="border-t border-neutral-200 dark:border-neutral-800 pt-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-neutral-900 dark:text-white">آیتم‌های انتقال</h4>
          <Button type="button" variant="outline" size="sm" onClick={() => appendItem({ product_id: '', variant_id: null, quantity: 1 })}>
            <Plus className="w-4 h-4 ml-1" />
            افزودن آیتم
          </Button>
        </div>

        {itemFields.map((field, index) => (
          <div key={field.id} className="card p-4 mb-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-medium text-neutral-900 dark:text-white">آیتم #{index + 1}</span>
              {itemFields.length > 1 && (
                <button type="button" onClick={() => removeItem(index)} className="p-1.5 rounded-lg text-neutral-400 hover:text-danger-600 dark:hover:text-danger-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Controller
                name={`items.${index}.product_id`}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select label="محصول *" options={[{ value: '', label: 'انتخاب کنید' }]} placeholder="محصول" {...field} />
                )}
              />
              <Controller
                name={`items.${index}.variant_id`}
                control={control}
                render={({ field }) => (
                  <Select label="تنوع" options={[{ value: '', label: 'پیش‌فرض' }]} placeholder="تنوع" {...field} />
                )}
              />
              <Controller
                name={`items.${index}.quantity`}
                control={control}
                rules={{ required: true, min: 1 }}
                render={({ field }) => (
                  <Input label="تعداد *" type="number" min={1} step={1} {...field} />
                )}
              />
            </div>
          </div>
        ))}

        {itemFields.length === 0 && (
          <div className="text-center py-8 border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-xl">
            <Package className="w-12 h-12 text-neutral-400 mx-auto mb-2" />
            <p className="text-neutral-500 dark:text-neutral-400">هیچ آیتمی اضافه نشده است</p>
            <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => appendItem({ product_id: '', variant_id: null, quantity: 1 })}>
              <Plus className="w-4 h-4 ml-1" />
              افزودن آیتم اول
            </Button>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-800">
        <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>انصراف</Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? <> <Loader2 className="w-4 h-4 animate-spin" /> در حال ثبت... </> : <> <Send className="w-4 h-4 ml-1" /> ثبت انتقال </> }
        </Button>
      </div>
    </form>
  )
}

type TransferListParams = {
  page: number
  page_size: number
  sort_by: string
  sort_order: 'asc' | 'desc'
  search?: string
  status?: string
  from_warehouse_id?: string
  to_warehouse_id?: string
  date_from?: string
  date_to?: string
}